/**
 * What survives when the buyer commits to a payment rail.
 *
 * A session holds exactly ONE invoice. Committing to a rail therefore VOIDS the other rail's
 * invoice: `reconcileCheckoutInvoice` cancels it and mints a replacement on the rail being
 * committed to. Anything the checkout is still holding from the voided invoice is dead the
 * instant that happens, and both leftovers are harmful:
 *
 * - A Stripe **client secret** points at a PaymentIntent on a cancelled invoice. Mounting Elements
 *   against it makes Stripe throw on load — the checkout looks broken through no fault of the
 *   buyer's, and the card rail cannot be used again without a reload.
 * - A **CLABE** is worse. It stays perfectly plausible on screen, so a buyer who transfers to it
 *   sends REAL MONEY to an account that will never be reconciled against this order.
 *
 * So the rule is: committing to a rail forgets the other one. It lives here, pure and tested,
 * because it is the kind of rule that is easy to half-apply — the original bug was exactly that,
 * the bank-transfer commit updating its own screen and silently leaving the card rail holding a
 * secret it had just invalidated.
 */
import {
  GATE2WAY_PROVIDER,
  STRIPE_PROVIDER,
  isInstructionRail,
  type CheckoutProvider,
} from '@/utils/session-invoice';
import type { SpeiTransferDetails } from '../data/spei-details';
import type { PixDetails } from '../data/pix-details';

/** Which screen the checkout is on. The card rail never leaves `form` — a card is entered and
 *  charged in place — so this only really branches for the instruction rails (SPEI, PIX). */
export type CheckoutView = 'form' | 'instructions';

/** How far the card rail has got towards being chargeable. */
export type CardPhase =
  /** Not selected yet, so nothing has been asked of the backend. */
  | 'idle'
  /**
   * Selected, but starting it would VOID bank coordinates the buyer is already holding, so it
   * waits for them to say so. See {@link needsSupersedeConfirm}.
   */
  | 'confirm-supersede'
  /** Reconciling the invoice / fetching the client secret. */
  | 'preparing'
  /** Stripe Elements can mount and take a card. */
  | 'ready'
  /** The invoice or the secret could not be obtained; the buyer is offered a retry. */
  | 'error';

/** What an instruction rail shows the buyer to pay against, tagged with the rail it belongs to so
 *  a commit can tell its own details from another rail's. */
export type InstructionDetails =
  | { rail: 'commerce-gate'; spei: SpeiTransferDetails }
  | { rail: 'gate2way'; pix: PixDetails };

/** Everything the checkout holds that BELONGS to the session's current invoice. */
export interface RailArtifacts {
  cardPhase: CardPhase;
  /** Stripe: the PaymentIntent client secret. */
  clientSecret: string | null;
  /** Instruction rail: the coordinates (SPEI) or the code (PIX) to pay to. */
  details: InstructionDetails | null;
  view: CheckoutView;
}

/** The rails a commit to `committed` supersedes: every offered rail but the committed one. */
export const otherRails = (committed: CheckoutProvider, methods: readonly CheckoutProvider[]): CheckoutProvider[] =>
  methods.filter((m) => m !== committed);

/**
 * Drop whatever belonged to the rail that `committed` has just superseded.
 *
 * Total and pure: the same artifacts and rail always give the same result, and the input is never
 * mutated. The committed rail's own artifacts are left ALONE — the caller has just resolved them,
 * and clearing them here would undo the very work that triggered this.
 */
export function supersedeRail(artifacts: RailArtifacts, committed: CheckoutProvider): RailArtifacts {
  if (!isInstructionRail(committed)) {
    // The instruction rail's invoice is gone: forget its details, and leave the instructions
    // screen, which would otherwise be showing an account or code nobody should pay into.
    return { ...artifacts, details: null, view: 'form' };
  }
  // The card's invoice is gone: forget the secret and put the rail back to where selecting it
  // asks for a fresh one.
  const next: RailArtifacts = { ...artifacts, clientSecret: null, cardPhase: 'idle' };
  // …and so is the OTHER instruction rail's, if its details are what is on screen.
  if (artifacts.details != null && artifacts.details.rail !== committed) {
    return { ...next, details: null, view: 'form' };
  }
  return next;
}

/**
 * Whether the card rail should recover now that the buyer has typed a usable email.
 *
 * The card rail refuses to start without one (the invoice is billed to the address on the
 * session), and recovering on its own beats making the buyer hunt for a retry button over a field
 * they have just fixed. But recovery is a COMMIT to the card rail, and committing supersedes the
 * bank transfer's invoice.
 *
 * So the current rail is part of the condition, not context the caller can be trusted to have
 * checked. Nothing clears the "blocked on email" latch when the buyer switches rails, so without
 * this an address typed on the TRANSFER form re-entered the card rail and cancelled a CLABE the
 * buyer may already have been paying against — the exact loss {@link supersedeRail} exists to
 * prevent, arriving through the one door that bypassed it.
 */
export function shouldRecoverCardOnEmail(
  method: CheckoutProvider,
  blockedOnEmail: boolean,
  emailValid: boolean,
): boolean {
  return method === STRIPE_PROVIDER && blockedOnEmail && emailValid;
}

/** What the checkout knows about its session and its entry probe. */
export interface CheckoutRestoreState {
  /** `/session/load` has reached a verdict — resolved OR rejected. */
  sessionSettled: boolean;
  /** `/session/load` resolved SUCCESSFULLY. A rejection settles without ever loading. */
  sessionLoaded: boolean;
  /** There is a session id to restore from (rehydrated synchronously from localStorage). */
  hasSession: boolean;
  /** The entry probe has finished reading where the buyer left off. */
  probeSettled: boolean;
}

/**
 * Whether the checkout must stay closed while it works out where the buyer left off.
 *
 * The payment pane is held shut on this, so it MUST always reach a decision: a `restoring` that
 * cannot clear is a checkout nobody can pay from.
 *
 * The case that broke that promise is a session load which settled by FAILING. `sessionLoaded`
 * stays false forever while `sessionId` remains rehydrated from localStorage, and the entry probe
 * is gated on `sessionLoaded` — so `probeSettled` never comes, and waiting on it deadlocks the
 * spinner. `noSession` does not rescue it either, because there IS a session id. When the load
 * settled without loading, there is nothing left to wait for: release, and let the checkout render
 * its normal state.
 */
export function isCheckoutRestoring(s: CheckoutRestoreState): boolean {
  if (!s.sessionSettled) return true;
  if (!s.hasSession) return false;
  // Settled but not loaded = it failed. No probe will ever run, so waiting is a deadlock.
  // {@link hasSessionLoadFailed} is what the caller must render instead — NOT the payment form.
  if (!s.sessionLoaded) return false;
  return !s.probeSettled;
}

/**
 * Whether `/session/load` settled by FAILING, leaving the checkout with a session id but no
 * session data.
 *
 * Releasing {@link isCheckoutRestoring} for this state fixed a deadlock, but releasing it INTO the
 * payment form would be worse than the deadlock it replaced. The store still holds the initial
 * empty `data`, so a submit from that form would `POST /session/save` a one-key object over the
 * buyer's real configuration — the wizard's own save call sites are all gated on `loaded` for
 * exactly this reason — and then reconcile an invoice against an empty selection, cancelling a
 * pending one whose CLABE the buyer may already hold.
 *
 * So this is its own screen: the retryable error. Unlike `noSession` (nothing to bill, a refetch
 * cannot conjure one) a failed load is usually transient, so the buyer is offered a retry.
 */
export function hasSessionLoadFailed(s: Pick<CheckoutRestoreState, 'sessionSettled' | 'sessionLoaded'>): boolean {
  return s.sessionSettled && !s.sessionLoaded;
}

/**
 * Whether committing to `next` would destroy something the buyer is ALREADY acting on, and must
 * therefore ask first.
 *
 * The rails supersede each other silently by design ({@link supersedeRail}), which is right when
 * the rail being dropped holds nothing the buyer can act on. Instruction details on screen are the
 * exception, whichever rail they belong to: money may already be in flight against them — a CLABE
 * pasted into a banking app (SPEI settles over minutes-to-a-business-day, so the invoice sits
 * `pending` and looks fine), a PIX QR already scanned. A plain radio click on any OTHER rail would
 * cancel that invoice out from under the buyer, and money in flight to a cancelled invoice is never
 * reconciled against the order.
 *
 * So any commit away from the rail whose details are on screen asks first. Committing to the rail
 * that owns them does not, and neither does dropping a Stripe client secret: the buyer cannot be
 * mid-payment against one (the charge is confirmed in this browser, in one step).
 */
export function needsSupersedeConfirm(next: CheckoutProvider, artifacts: Pick<RailArtifacts, 'details'>): boolean {
  return artifacts.details != null && artifacts.details.rail !== next;
}

/** Whether committing to `provider` must always mint a fresh invoice rather than reuse a pending
 *  one: a PIX code is never reused (spec 4.7). */
export const reissuesOnCommit = (provider: CheckoutProvider): boolean => provider === GATE2WAY_PROVIDER;

/**
 * The screen a returning buyer is restored to. Only SPEI restores INTO its instructions, and only
 * when there are details to show and the buyer had reached that screen before; PIX always restores
 * to the form, because its code is reissued on commit ({@link reissuesOnCommit}).
 */
export function restoreViewFor(s: {
  provider: CheckoutProvider;
  hasDetails: boolean;
  reachedInstructions: boolean;
}): CheckoutView {
  return s.provider === 'commerce-gate' && s.hasDetails && s.reachedInstructions ? 'instructions' : 'form';
}

/** Whether Back must ask first: leaving the PIX instructions screen abandons a code the buyer may
 *  already have scanned. */
export const needsBackConfirm = (method: CheckoutProvider, view: CheckoutView): boolean =>
  method === GATE2WAY_PROVIDER && view === 'instructions';

/**
 * The auto-advance rule (spec 4.7): the checkout moves on by itself once the invoice it is SHOWING
 * is paid. PIX only, by scope.
 *
 * The id equality is what makes this "the invoice shown is paid" rather than "some invoice is
 * paid": a push about a superseded invoice must never advance the buyer. The slice guard already
 * drops pushes for other ids; this is the checkout's own half of that contract.
 */
export function isShownInvoicePaid(s: {
  rail: CheckoutProvider | null;
  view: CheckoutView;
  shownInvoiceId: string | null;
  sessionInvoiceId: string | null;
  invoiceStatus: string | null;
}): boolean {
  return (
    s.rail === GATE2WAY_PROVIDER &&
    s.view === 'instructions' &&
    s.shownInvoiceId != null &&
    s.shownInvoiceId === s.sessionInvoiceId &&
    s.invoiceStatus === 'paid'
  );
}

/**
 * Whether the card rail may start reconciling an invoice on its own, without the buyer having
 * chosen it in this visit.
 *
 * Restoring INTO the card rail is treated as the same commitment as selecting it, which is what
 * makes a returning buyer's checkout come back chargeable. But that commitment MINTS — it POSTs
 * `/session/invoice`, and can cancel and reissue on the way. Doing that on mount, before the
 * already-paid check has answered, risks asking for money a second time on a session that was
 * settled before the checkout even opened.
 *
 * So an unprompted start waits for that answer; a buyer who actually clicks the rail does not
 * (their click is the answer, and making them watch a spinner for a read they did not ask for is
 * the wrong trade).
 *
 * NOTE the residual: a paid session whose invoice was SUPERSEDED cannot be recognised as paid at
 * all, because `GET /session/invoice` returns the superseded row and there is no way to resolve
 * the live one. That is the same backend limitation as the deploy gate's fail-open, and it is not
 * closeable here.
 */
export function mayAutoPrepareCard(s: { chosenExplicitly: boolean; paidCheckAnswered: boolean }): boolean {
  return s.chosenExplicitly || s.paidCheckAnswered;
}

/**
 * The rail to restore from what the session remembers the buyer last selected — only when the
 * session still OFFERS it. A saved rail can outlive the currency it was chosen under (SPEI saved
 * on MXN, then the buyer switches to BRL); restoring it would render an instruction form for a
 * rail the picker no longer shows and POST that provider on a session that cannot take it.
 */
export function restorableRail(
  saved: unknown,
  methods: readonly CheckoutProvider[],
): CheckoutProvider | null {
  return methods.find((m) => m === saved) ?? null;
}
